import { useState, useEffect, useCallback, useRef } from 'react';
import { getPoranaytteetByTarkastuskohdeV2, getTestitV2, createTestiV2, updateTestiV2, getTestiCaptionV2, updateTestiCaptionV2 } from '../../../../../api/api';
import useApiFeedback from './useApiFeedback';
import { parseCaptionForEditor, serializeCaptionForSave } from './captionUtils';

// Shared service for the three "one testi-type per poränäyte" blocks
// (Ohuthie, Kloridi, Vetolujuus) — same data shape and CRUD for all three,
// only `tyyppi` and the default `arvo` shape differ per caller.
//
// Unlike Poränäyte/Peitepaksuus (inherently per-rakenneosa), these tests
// apply across a whole tarkastuskohde, so this is scoped by tkId, not rakId
// — it lists samples from every rakenneosa under the tarkastuskohde (each
// tagged with its source rakenneosa) rather than one rakenneosa tab's worth.
//
// Row lifecycle is entirely driven by Poränäyte, not by these blocks: a row
// auto-creates the moment a sample becomes eligible (checked in Poränäyte),
// and unchecking it there deletes the row (see usePoranayteV2.toggleTesti).
// These blocks only ever edit an existing row's values — addRow here is
// purely a manual retry if auto-create failed (e.g. a network blip), not a
// general "add another row" action.
export default function useTestiTypeV2(tkId, tyyppi, defaultArvo) {
  const [samples, setSamples] = useState([]);
  const [rows, setRows] = useState({}); // poraId -> testi rows of this tyyppi
  const [captionRow, setCaptionRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const { feedback, run, clearFeedback } = useApiFeedback();

  // loading only tracks the tkId-triggered load (below), not the per-edit
  // refreshes addRow/patchArvo also trigger — otherwise every edit would
  // flicker the table's spinner.
  useEffect(() => { if (tkId) setLoading(true); }, [tkId]);

  // One caption per (tarkastuskohde, tyyppi) — shared by every row in this
  // table regardless of which sample/rakenneosa it came from. See
  // testi_caption on the backend. Keeps the whole row (not just .caption)
  // so the view can key its editor's remount off captionRow.id — the
  // already-loaded row — rather than off tkId itself, which changes
  // synchronously before this fetch resolves and would otherwise remount
  // the editor onto still-stale text (see PeitepaksuusView for the same fix).
  const refreshCaption = useCallback(() => {
    if (!tkId) { setCaptionRow(null); return; }
    getTestiCaptionV2(tkId, tyyppi).then(r => setCaptionRow(r.data)).catch(() => {});
  }, [tkId, tyyppi]);
  useEffect(refreshCaption, [refreshCaption]);

  // TaulukkoFooter's onChange fires on every keystroke (Lexical's
  // OnChangePlugin fires per edit, not on blur) — debounce the actual save
  // (same 600ms pattern Poränäyte's Lisähuomioita field uses), and merge
  // the saved value into captionRow directly on success rather than
  // refetching — the PATCH response already confirms what was saved.
  const captionTimer = useRef(null);
  const commitCaption = useCallback((capData) => {
    if (!tkId) return;
    const targetRowId = captionRow?.id ?? null; // guards against switching tarkastuskohde mid-debounce
    const serialized = serializeCaptionForSave(capData);
    clearTimeout(captionTimer.current);
    captionTimer.current = setTimeout(async () => {
      try {
        await run(() => updateTestiCaptionV2(tkId, tyyppi, serialized));
        setCaptionRow(prev => prev && prev.id === targetRowId ? { ...prev, caption: serialized } : prev);
      } catch { /* captured in feedback */ }
    }, 600);
  }, [tkId, tyyppi, run, captionRow]);

  const refresh = useCallback(async () => {
    if (!tkId) { setSamples([]); setRows({}); return; }
    const s = (await getPoranaytteetByTarkastuskohdeV2(tkId)).data;
    setSamples(s);
    const eligible = s.filter(x => (x.testi || '').toLowerCase().includes(tyyppi.toLowerCase()));
    const entries = await Promise.all(eligible.map(async x => {
      let r = (await getTestitV2(x.id)).data.filter(t => t.tyyppi === tyyppi);
      if (r.length === 0) {
        await createTestiV2(x.id, tyyppi, defaultArvo).catch(() => {});
        r = (await getTestitV2(x.id)).data.filter(t => t.tyyppi === tyyppi);
      }
      return [x.id, r];
    }));
    setRows(Object.fromEntries(entries));
  }, [tkId, tyyppi, defaultArvo]);
  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  const addRow = useCallback(async (sample) => {
    try { await run(() => createTestiV2(sample.id, tyyppi, defaultArvo)); }
    catch { /* captured in feedback */ }
    finally { refresh(); }
  }, [tyyppi, defaultArvo, run, refresh]);

  const patchArvo = useCallback(async (row, field, value) => {
    try { await run(() => updateTestiV2(row.id, { arvo: { ...row.arvo, [field]: value } })); }
    catch { /* captured in feedback */ }
    finally { refresh(); }
  }, [run, refresh]);

  const eligible = samples.filter(s => (s.testi || '').toLowerCase().includes(tyyppi.toLowerCase()));

  return { samples, eligible, rows, loading, feedback, clearFeedback, addRow, patchArvo, caption: parseCaptionForEditor(captionRow?.caption), captionKohdeId: captionRow?.id ?? null, commitCaption };
}
